<template>
  <w-modal
    v-model="show"
    :submit-button="$t('buttons.save')"
    :title="$t(title)"
    :close-button="$t('buttons.cancel')"
    :close-on-submit="false"
    :hideOnOverlay="false"
    :hideOnEsc="false"
    @submit="save"
    @close="close"
  >
    <div class="row">
      <div class="form-group col-md-6">
        <w-select
          v-if="examId"
          :model="$schema.profileId"
          :values="players"
          :title="$t($schema.profileId.$schema.title)"
        />

        <w-select
          v-else
          :model="$schema.examId"
          :values="exams"
          :title="$t($schema.examId.$schema.title)"
          @input="selectExam"
        />
      </div>

      <div class="form-group col-md-6">
        <form-datepicker :model="$schema.date" :title="$t($schema.date.$schema.title)" />
      </div>
    </div>

    <template v-if="$schema.examUnit.$model">
      <div class="row" v-if="ExamUnitType.TIME !== $schema.examUnit.$model">
        <div class="form-group col-md-12">
          <w-input :model="$schema.score" :title="scoreTitle" />
        </div>
      </div>

      <span v-else>
        <time-input :model="$schema.timeInput" />

        <small class="text-sm text-danger">{{ $schema.score.$error }}</small>
      </span>
    </template>
  </w-modal>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';

import { manageMixin } from '@/mixins/manage';
import TimeInput from '@/components/TimeInput';
import schema from '@/schemas/examResult.schema';
import { ExamUnitType, ExamUnitTypeScoreTranslation } from '@/enums/examUnitType.enum';
import { tryParseInt } from '@/utils/number';
import { TableName } from '@/enums/tableName.enum';
import { secondsToTimeInput } from '@/utils/time';

const timeInput = () => ({
  hour: 0,
  minute: 0,
  second: 0,
  hundredth: 0
});

const emptyData = () => ({
  date: '',
  score: null,
  profileId: null,
  examId: null,
  examUnit: null,
  teamId: null,
  timeInput: timeInput()
});

// @vuese
// Modal wyników egzaminu
// @group Components
export default {
  mixins: [manageMixin('exam/results', {}, emptyData)],
  schema,
  data() {
    return { ExamUnitType };
  },

  methods: {
    // @vuese
    // Ustawia dane formularza
    setData() {
      const data = this.id ? this.stateData : emptyData();
      data.examUnit = this.examUnit(this.id && this.stateData.examId);
      this.$schema.$setData(data);
      this.$schema.teamId.$model = tryParseInt(this.teamId);

      if (this.examId) {
        this.$schema.examId.$model = tryParseInt(this.examId);
      } else {
        this.$schema.profileId.$model = tryParseInt(this.profileId);
      }

      const isTime = this.id && this.$schema.examUnit.$model === ExamUnitType.TIME;
      this.$schema.timeInput.$setData(isTime ? secondsToTimeInput(this.$schema.score.$model) : timeInput());

      this.$schema.$reset();
    },
    // @vuese
    // Wybiera egzamin
    selectExam(id) {
      this.$schema.$setData({ examUnit: this.examUnit(tryParseInt(id)), score: null, timeInput: timeInput() });
      this.$schema.$reset();
    },

    // @vuese
    // Wyszukuje jednostke egzaminu na podstawie egzaminu
    findExamUnit(examId) {
      const selectedExam = this.exams.find(exam => exam.id === examId);
      return selectedExam && selectedExam.unit;
    },

    // @vuese
    // Zwraca aktualną jednostkę egzaminu
    examUnit(examId) {
      if (this.examId) {
        return this.exam.unit;
      }
      if (examId) {
        return this.findExamUnit(examId);
      }
      return null;
    }
  },
  computed: {
    ...mapState({
      players: state =>
        state.team.player.list.map(player => ({ value: player.id, text: `${player.firstName} ${player.lastName}` })),
      exams: state => state.exam.list.map(exam => ({ ...exam, value: exam.id, text: exam.name })),
      exam: state => state.exam.data,
      stateData: state => state.exam.results.data
    }),
    title() {
      return this.id ? 'modals.exam-result-edit' : 'modals.exam-result-add';
    },
    scoreTitle() {
      return this.$t(ExamUnitTypeScoreTranslation[this.$schema.examUnit.$model]);
    },
    profileId() {
      return this.$route.params.playerId;
    },
    examId() {
      return this.$route.params.examId;
    },
    teamId() {
      return this.$route.params.teamId;
    }
  },
  components: {
    TimeInput
  }
};
</script>
